import { DataQualityService, PortfolioService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DataQualityGetRule } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Checkbox, CheckboxProps, Form, FormField, Modal, NonCancelableCustomEvent, Select, 
    SelectProps, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { DropdownStatusProps } from '@amzn/awsui-components-react/polaris/internal/components/dropdown-status';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';

import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { deepCopy } from '../../common/util';
import { generateRulePluralStr, getImpactBadgeClassNames, getImpactLabel, sortImpacts } from './data-quality-helper';
import { RuleToDisplay } from './DataQualityRuleValidation';

interface DataQualityCopyRulesModalProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    assetType: string;
    currentRules: RuleToDisplay[];
    onCopyComplete: () => void;
}

const portfolioService = new PortfolioService();
const dataQualityService = new DataQualityService();

function DataQualityCopyRulesModal(
    {assetType, setVisible, visible, currentRules, onCopyComplete, flash}: DataQualityCopyRulesModalProps & FlashContextInterface
): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;

    const [portfoliosLoadingStatus, setPortfoliosLoadingStatus] = useState<DropdownStatusProps.StatusType>();
    const [portfolios, setPortfolios] = useState<SelectProps.Options>();
    const [selectedPortfolio, setSelectedPortfolio] = useState<OptionDefinition | null>(null);
    const [rulesToCopy, setRulesToCopy] = useState<RuleToDisplay[]>([]);
    const [rulesToCopyLoading, setRulesToCopyLoading] = useState(false);
    const [isCopyRulesButtonDisabled, setIsCopyRulesButtonDisabled] = useState(true);
    const [areAllRulesSelected, setAreAllRulesSelected] = useState(false);
    const [copyLoading, setCopyLoading] = useState(false);

    useEffect(() => {
        const loadPortfolios = async () => {
            setPortfolios([]);
            setPortfoliosLoadingStatus('loading');
            try {
                const allPortfolios = await portfolioService.listPortfolios();
                const portfolioOptions = allPortfolios.filter(portfolio => portfolio.id !== portfolioId)
                    .map((portfolio): OptionDefinition => {
                        return {
                            value: portfolio.id,
                            label: portfolio.name,
                        };
                    });
                setPortfolios(portfolioOptions);
                setPortfoliosLoadingStatus('finished');
            } catch (err) {
                setPortfoliosLoadingStatus('error');
            }
        };
        loadPortfolios();
    }, [portfolioId]);

    const setCopyRulesButtonEnabledOrDisabled = () => {
        rulesToCopy.find(rule => rule.selected) ? setIsCopyRulesButtonDisabled(false) : setIsCopyRulesButtonDisabled(true);
    };

    useEffect(() => {
        setCopyRulesButtonEnabledOrDisabled();
        rulesToCopy.find(rule => !rule.selected && !rule.errorMsg) ? setAreAllRulesSelected(false) : setAreAllRulesSelected(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [rulesToCopy]);

    useEffect(() => {
        const loadCopyRules = async () => {
            if (!selectedPortfolio || !selectedPortfolio.value) {
                return;
            }
            setRulesToCopyLoading(true);
            try {
                const rules: DataQualityGetRule[] = await dataQualityService.listRules(selectedPortfolio.value, assetType);
                const rulesToDisplay: RuleToDisplay[] = rules.map(rule => {
                    const displayRule: RuleToDisplay = Object.assign({selected: false}, rule);
                    displayRule.impacts?.sort(sortImpacts);
                    return displayRule;
                }).filter(rule => !rule.isDefault);

                rulesToDisplay.forEach(rule => {
                    let matchingRule = currentRules.find(currentRule => currentRule.name === rule.name && currentRule.ruleId !== rule.ruleId);
                    if (matchingRule) {
                        rule.errorMsg = constants.RULE_COPY_ERROR_MSG;
                    }
                    matchingRule = currentRules.find(currentRule => currentRule.ruleId === rule.ruleId);
                    if (matchingRule) {
                        rule.warningMsg = constants.RULE_COPY_WARNING_MSG;
                    }
                });
                setRulesToCopy(rulesToDisplay);
            } catch (err) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_RULES);
            } finally {
                setRulesToCopyLoading(false);
            }
        };
        setRulesToCopy([]);
        setIsCopyRulesButtonDisabled(true);
        loadCopyRules();
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [selectedPortfolio, assetType, currentRules]);

    const dismissModal = (): void => setVisible(false);

    const copyRules = async () => {
        if (!portfolioId || !selectedPortfolio || !selectedPortfolio.value) {
            return;
        }
        setCopyLoading(true);
        const selectedRules = rulesToCopy.filter(rule => rule.selected);
        const ruleIds = selectedRules.map(rule => rule.ruleId);
        try {
            await dataQualityService.copyRules(portfolioId, selectedPortfolio.value, ruleIds);
            flash.alert(FlashType.success, `${constants.SUCCESS_COPYING_RULES}${generateRulePluralStr(ruleIds.length)}`, constants.SUCCESS);
            onCopyComplete();
        }
        catch(err) {
            flash.alert(FlashType.error, (err as Error).message, `${constants.ERROR_COPYING_RULE}${generateRulePluralStr(ruleIds.length)}`);
        }
        finally {
            setCopyLoading(false);
            dismissModal();
        }
    };

    const onPortfolioSelectionChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setSelectedPortfolio(event.detail.selectedOption);
    };

    const selectAllRulesChanged =  (event: NonCancelableCustomEvent<CheckboxProps>): void => {
        rulesToCopy.forEach(rule => {
            if (!rule.errorMsg) {
                rule.selected = event.detail.checked;
            }
        });
        setCopyRulesButtonEnabledOrDisabled();
        setAreAllRulesSelected(event.detail.checked);
    };

    const ruleSelectedChanged = (rule: RuleToDisplay) => (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>) => {
        const rules: RuleToDisplay[] = deepCopy(rulesToCopy);
        const changedRule = rules.find(r => r.ruleId === rule.ruleId);
        if (!changedRule) {
            return;
        }
        changedRule.selected = event.detail.checked;
        setRulesToCopy(rules);
    };

    const rulesHeader = (
        <li className="validation-head copy">
            <div><Checkbox checked={areAllRulesSelected} onChange={selectAllRulesChanged}/></div>
            <div>{constants.VALIDATION_RULE}</div>
            <div>{constants.IMPACT_AREA}</div>
            <div>{constants.WARNINGS}</div>
        </li>
    );

    const getRuleListContent = () => {
        const ruleList = rulesToCopy.map(rule => {
            const className = rule.errorMsg ? 'validation-row copy error' : 'validation-row copy';
            return (
                <li className={className} key={rule.ruleId}>
                    <div>
                        <Checkbox 
                            checked={rule.selected}
                            onChange={ruleSelectedChanged(rule)}
                            disabled={rule.errorMsg ? true : false}
                        />
                    </div>
                    <div>
                        <strong>{rule.name}</strong>
                        {rule.isDefault ? null : <span className="is-custom-rule">{constants.CUSTOM}</span>}
                    </div>
                    <div>
                        {
                            rule.impacts?.map(impact => 
                                <div key={impact.area}><span 
                                    className={getImpactBadgeClassNames(null, rule, impact.area)}
                                >
                                    {getImpactLabel(impact.area)}
                                </span></div>
                            )
                        }
                    </div>
                    <div>
                        {rule.errorMsg || rule.warningMsg}
                    </div>
                </li>
            );
        });
        return (
            <div>
                {ruleList}
            </div>
        );
    };

    const rulesPopulatedContent = rulesToCopy && rulesToCopy.length > 0 ? (
        <FormField
            label={constants.SELECT_COPY_RULE_HEADER}
            stretch
        >
            <ul className="validation-table copy">
                {rulesHeader}
                {getRuleListContent()}
            </ul>
        </FormField>
    ) : (
        <div>{constants.NO_CUSTOM_RULES}</div>
    );

    const rulesContent = rulesToCopyLoading ? (
        <Spinner/>
    ) : (
        rulesPopulatedContent
    );

    const content = (
        <Form>
            <SpaceBetween size='m'>
                <FormField
                    label={constants.SELECT_PORTFOLIO}
                >
                    <Select
                        statusType={portfoliosLoadingStatus}
                        loadingText={constants.LOADING_PORTFOLIOS}
                        errorText={constants.LOADING_PORTFOLIOS_ERROR}
                        empty={constants.NO_PORTFOLIOS_FOUND}
                        options={portfolios}
                        selectedOption={selectedPortfolio}
                        onChange={onPortfolioSelectionChange}
                        filteringType='auto'
                    />
                </FormField>
                {selectedPortfolio ? rulesContent : null}
            </SpaceBetween>
        </Form>
    );

    const copyRulesFooter = (
        <Box float='right'>
            <SpaceBetween size='m' direction='horizontal'>
                <Button
                    loading={copyLoading}
                    onClick={dismissModal}
                >
                    {constants.CANCEL_BUTTON_TEXT}
                </Button>
                <Button
                    variant='primary'
                    disabled={isCopyRulesButtonDisabled}
                    onClick={copyRules}
                    loading={copyLoading}
                >
                    {constants.IMPORT_RULES}
                </Button>
            </SpaceBetween>
        </Box>
    );

    return (
        <Modal
            visible={visible}
            size='large'
            header={constants.COPY_RULE_HEADER}
            footer={copyRulesFooter}
            onDismiss={dismissModal}
        >
            {content}
        </Modal>
    );
}

export default withFlashContext(DataQualityCopyRulesModal);
